import React, { useState, useRef, useEffect, useId } from 'react';
import {
  Container,
  Progress,
  Card,
  CardBody,
  Row,
  Col,
  Collapse,
} from 'reactstrap';
import { FaCheck, FaLock, FaLockOpen } from 'react-icons/fa';
import { useUserAuth } from "../../context/UserContext";
import { isPrivilegedRole } from "../../utils/roles";
import { getFreshIdToken } from "../../utils/authToken";
import { requestLessonsData } from "../../services/userService";
import DemoNavbar from "../../components/Navbars/DemoNavbar.js";
import useReveal from "../../hooks/useReveal";
import { V4_MODULES } from "./devV4LessonsRegistry";
// Grouping and lock logic live in their own module so they can be unit-tested: this
// component imports UserContext, which imports firebase/auth, which will not load
// under jsdom. See v4ModuleGating.js for the Module 5 / Module 5.5 unlock rule.
import {
  buildModules,
  applyLocks,
  findActiveModuleIndex,
} from "./v4ModuleGating";
import '../examples/TableOfContent.css';
import { useTranslation } from "react-i18next";
import { TocLesson, LessonStatus, LockedNote, useLockedNote } from "../../components/CourseToc/CourseToc";

// Table of contents for "Claude AI Developer V4" (CD04).
//
// A near-copy of ClaudeDevV3TableComponent pointing at the V4 registry and
// courseID. Kept as a separate component rather than parameterising an existing
// one so the live courses' tables cannot be broken while V4 is iterated on.
//
// It no longer shares the V1–V3 look, though: this is the current course, so it
// gets the site's navy band and the .ctoc-* styles in TableOfContent.css. The
// older tables keep the shared .custom-card / .left-border-* classes.

// "01", "02"… — indices sit in a fixed-width mono column, so they line up.
const pad2 = (n) => String(n).padStart(2, "0");

const ClaudeDevV4TableComponent = () => {
  const mainRef = useRef(null);
  const { user } = useUserAuth();
  const { t } = useTranslation();

  // Privileged roles (admin/tester) see every lesson unlocked regardless of
  // completion, so they can navigate the whole course freely for QA.
  const privileged = isPrivilegedRole(user?.role);

  // Modules built from the registry; lock state seeded so Module 1 / Lesson 1 is open on first paint.
  const [modules, setModules] = useState(() => applyLocks(buildModules()));

  const allLessons = modules.flat();
  const completedCount = allLessons.filter((l) => l.completed).length;
  const totalCount = allLessons.length;
  const progress = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  // Derived rather than state so a manual expand/collapse isn't fought over: this
  // only changes when `modules` does, i.e. when progress lands from the fetch.
  const activeModuleIndex = findActiveModuleIndex(modules);

  // Scroll reveal for the band and the module cards. Every module card is built
  // from the registry, so all of them are in the DOM on the first paint and one
  // scan covers the page.
  useReveal();

  // Keyed on the user id so the fetch re-runs when auth resolves: on a client-side
  // navigation the component can paint before `user` is populated, and a one-shot
  // effect would bail out silently and never retry — which reads as 0% until the
  // page is reloaded.
  useEffect(() => {
    let cancelled = false;

    const loadProgress = async () => {
      if (!user) return;

      // Mint a fresh token rather than reusing the (possibly hours-old) snapshot on
      // `user`: an expired token fails verification server-side and requestLessonsData
      // swallows the error into [], which reads as "nothing completed".
      const token = await getFreshIdToken(user.accessToken);
      if (!token || cancelled) return;

      const result = await requestLessonsData(token, "CD04");
      if (cancelled) return;

      const completedIDs = new Set(
        (result || []).filter((r) => r.lessonCompleted === true).map((r) => r.lessonID)
      );

      // Rebuild from the registry, mark completion from the fetch, then re-apply gating.
      const merged = buildModules().map((mod) =>
        mod.map((l) => ({ ...l, completed: completedIDs.has(l.lessonID) }))
      );
      setModules(applyLocks(merged));
    };

    loadProgress();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  return (
    <>
      <DemoNavbar />
      <main ref={mainRef} className="ctoc">
        {/* The navy band: course title on the left, progress on the right.
            DemoNavbar stays solid and in normal flow above it, so there is no
            transparent-navbar override and no body class to add here. */}
        <section className="ctoc-hero">
          <span className="cn-grain" aria-hidden="true" />
          <span className="ctoc-orb ctoc-orb-1" aria-hidden="true" />
          <span className="ctoc-orb ctoc-orb-2" aria-hidden="true" />
          <Container>
            <Row className="ctoc-hero-row">
              <Col lg="7" data-reveal>
                <span className="ctoc-eyebrow">
                  <span className="ctoc-dot" aria-hidden="true" />
                  {t("table.tableOfContents")}
                </span>
                <h1 className="ctoc-title">{t("table.claudeDevV4TableT")}</h1>
                <p className="ctoc-sub">{t("table.completeLessonToUnlock")}</p>
              </Col>
              <Col lg="5" data-reveal style={{ "--reveal-delay": "120ms" }}>
                <div className="ctoc-stats">
                  <div className="ctoc-stats-row">
                    <span className="ctoc-stats-label" id="toc-progress-label">{t("table.yourProgress")}</span>
                    <span className="ctoc-stats-value ctoc-stats-value-lg">{progress}%</span>
                  </div>
                  <Progress
                    value={progress}
                    className="ctoc-progress"
                    barClassName="ctoc-progress-bar"
                    barAriaLabelledBy="toc-progress-label"
                    barAriaValueText={t("table.progressSummary", { done: completedCount, total: totalCount })}
                  />
                  <div className="ctoc-stats-row ctoc-stats-foot">
                    <span className="ctoc-stats-label">{t("table.totalLessons")}</span>
                    <span className="ctoc-stats-value">{totalCount}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="ctoc-body">
          <Container>
            {/* Title and number come from V4_MODULES rather than the index, so module
                metadata has one home alongside the gating rules. */}
            <div className="ctoc-modules">
              {modules.map((lessons, idx) => (
                <div
                  key={V4_MODULES[idx].moduleNum}
                  data-reveal
                  style={{ "--reveal-delay": `${Math.min(idx, 3) * 60}ms` }}
                >
                  <Module
                    title={V4_MODULES[idx].titleKey}
                    number={V4_MODULES[idx].moduleNum}
                    lessons={lessons}
                    privileged={privileged}
                    defaultOpen={idx === activeModuleIndex}
                  />
                </div>
              ))}
            </div>

            <Card className="ctoc-keep" data-reveal>
              <CardBody className="ctoc-keep-body">
                <div>
                  <h2 className="ctoc-keep-title">{t("table.keepGoing")}</h2>
                  <p className="ctoc-keep-text">
                    {t("table.youCompleted")} {completedCount} {t("table.of")} {totalCount} {t("table.lessons")} ({progress}%){t("table.completedAll")}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Container>
        </section>
      </main>
    </>
  );
};

const Module = ({ title, number, lessons, privileged, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [lockedNote, showLockedNote] = useLockedNote();
  const listId = useId();
  const { t } = useTranslation();

  // Which module is the active one is only known once progress has been fetched,
  // a paint or two after mount — so follow `defaultOpen` when it flips instead of
  // freezing whatever it was initially. It only flips on that fetch, so a manual
  // expand/collapse made afterwards is left alone.
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  // Display-only summaries for the header: how far through this module the
  // student is, and whether it is finished or still waiting on its prerequisite.
  const doneCount = lessons.filter((l) => l.completed).length;
  const moduleComplete = lessons.length > 0 && doneCount === lessons.length;
  const moduleLocked = !privileged && lessons.length > 0 && lessons.every((l) => l.locked);
  const modulePct = lessons.length ? Math.round((doneCount / lessons.length) * 100) : 0;

  const moduleClass = [
    "ctoc-module",
    open ? "is-open" : "",
    defaultOpen ? "is-current" : "",
    moduleComplete ? "is-complete" : "",
    moduleLocked ? "is-locked" : "",
  ].filter(Boolean).join(" ");

  return (
    <Card className={moduleClass}>
      <h2 className="toc-module-heading">
        <button
          type="button"
          className="toc-module-toggle ctoc-module-head"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(!open)}
        >
          <span className="ctoc-module-index">
            {moduleComplete ? <FaCheck aria-hidden="true" /> : pad2(number)}
          </span>
          <span className="ctoc-module-title">{t(title)}</span>
          <span className="ctoc-module-meta">
            <span className="ctoc-module-meter" aria-hidden="true">
              <span style={{ width: `${modulePct}%` }} />
            </span>
            <span className="ctoc-module-count" aria-hidden="true">{doneCount}/{lessons.length}</span>
            <span className="sr-only">{t("table.progressSummary", { done: doneCount, total: lessons.length })}</span>
            <svg className="ctoc-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
      </h2>
      <Collapse isOpen={open} id={listId}>
        <div className="ctoc-lessons">
          {lessons.map((lesson, idx) => {
            // Privileged users see everything unlocked regardless of completion.
            const locked = privileged ? false : lesson.locked;
            // The student's next lesson: open under the real gating and not done yet.
            const isNext = !lesson.locked && !lesson.completed;
            const lessonClass = [
              "ctoc-lesson",
              lesson.completed ? "is-complete" : "",
              locked ? "is-locked" : "",
              isNext ? "is-next" : "",
            ].filter(Boolean).join(" ");
            return (
              <TocLesson
                key={`${lesson.lessonID ?? lesson.title}-${idx}`}
                to={lesson.onClick}
                locked={locked}
                onLocked={showLockedNote}
                className={lessonClass}
                style={{ cursor: locked ? 'not-allowed' : 'pointer' }}
              >
                <span className="ctoc-lesson-index">
                  {lesson.completed ? <FaCheck aria-hidden="true" /> : pad2(idx + 1)}
                </span>
                <span className="ctoc-lesson-title">{t(`cdTitles.${lesson.lessonID}`, { defaultValue: lesson.title })}</span>
                <span className="ctoc-lesson-status">
                  {locked ? (
                    <FaLock className="ctoc-lock" aria-hidden="true" />
                  ) : (
                    <FaLockOpen className="ctoc-lock-open" aria-hidden="true" />
                  )}
                  <LessonStatus completed={lesson.completed} locked={locked} />
                </span>
              </TocLesson>
            );
          })}
          <LockedNote count={lockedNote} />
        </div>
      </Collapse>
    </Card>
  );
};

export default ClaudeDevV4TableComponent;
